import {Phone, Calendar, Shield, Sparkles, Check, MapPin, Award, Car, Info, Droplets, Sun, Clock, X} from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import CanonicalTag from '@/components/CanonicalTag'
import EmbeddedQuoteForm from '@/components/EmbeddedQuoteForm'

const CeramicCoatingSanDiegoPage = () => {
  const navigate = useNavigate()

  useEffect(() => {
    document.title = 'Ceramic Coating San Diego | Paint, Wraps & PPF Protection'
    const metaDesc = document.querySelector('meta[name="description"]')
    if (metaDesc) {
      metaDesc.setAttribute('content', 'The definitive ceramic coating guide for San Diego. Learn how ceramic coating protects paint, vinyl wraps, Colored Paint Protection Film, and PPF — hydrophobic, UV protection, gloss, longevity, and ceramic coating vs wax vs PPF. Call 858-282-8119.')
    }

    const faqScript = document.createElement('script')
    faqScript.type = 'application/ld+json'
    faqScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is ceramic coating?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Ceramic coating is a liquid polymer made primarily of silica (SiO2) that chemically bonds to a vehicle\'s surface, forming a durable, semi-permanent protective layer. Once cured, it creates a hydrophobic, UV-resistant, glossy shield that protects against contaminants, light scratches, oxidation, and chemical stains — far beyond what wax can offer.'
          }
        },
        {
          '@type': 'Question',
          name: 'How long does ceramic coating last in San Diego?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'A professionally applied ceramic coating typically lasts 2 to 5 years, with premium multi-layer coatings lasting even longer. In San Diego\'s sunny, coastal climate, longevity depends on UV exposure, wash habits, and maintenance. Proper care — including regular gentle washes and occasional toppers — maximizes lifespan.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is ceramic coating better than wax?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Wax lasts only weeks to a few months and provides minimal protection. Ceramic coating chemically bonds to the surface and lasts years, offering superior hydrophobic properties, UV defense, chemical resistance, and a deeper, longer-lasting gloss. Ceramic coating is the modern, longer-term paint protection solution.'
          }
        },
        {
          '@type': 'Question',
          name: 'Does ceramic coating prevent scratches and rock chips?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Ceramic coating adds hardness and resists light swirl marks and wash-induced scratches, but it does NOT stop rock chips or deep scratches. For physical impact protection against rock chips, Paint Protection Film (PPF) is required. Many San Diego owners combine PPF on high-impact areas with ceramic coating over the rest of the vehicle.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can you ceramic coat a vinyl wrap?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Ceramic coating can be applied over vinyl wraps to add hydrophobic protection, UV resistance, and easier cleaning while extending the life of the wrap. It helps protect satin and matte finishes from staining and makes the wrap easier to maintain. We use coatings formulated to be safe for wrap films.'
          }
        },
        {
          '@type': 'Question',
          name: 'Can you ceramic coat Colored Paint Protection Film?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Absolutely. Applying ceramic coating over Colored Paint Protection Film combines the rock chip and self-healing protection of Colored PPF with the hydrophobic, easy-clean, UV-resistant benefits of ceramic. This is one of the most complete protection packages available for high-value vehicles in San Diego.'
          }
        },
        {
          '@type': 'Question',
          name: 'Should I ceramic coat my PPF (Paint Protection Film)?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes — ceramic coating over PPF is highly recommended. While modern PPF already has self-healing properties, adding a ceramic topcoat enhances hydrophobicity, reduces staining, makes the film easier to clean, and boosts gloss. It is the ideal finishing layer for a fully protected vehicle.'
          }
        },
        {
          '@type': 'Question',
          name: 'Is ceramic coating worth it?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For most San Diego vehicle owners, yes. Ceramic coating dramatically reduces maintenance, protects against UV fading and water spots in our coastal/sunny climate, enhances gloss, and helps preserve resale value. It is a worthwhile investment for anyone who wants their vehicle to look its best with far less effort.'
          }
        },
        {
          '@type': 'Question',
          name: 'Does ceramic coating make a car easier to clean?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. The hydrophobic surface causes water, dirt, and grime to bead up and slide off rather than sticking. This means faster washes, fewer water spots, and less contamination buildup. Many owners find their coated vehicles stay cleaner longer between washes.'
          }
        }
      ]
    })
    document.head.appendChild(faqScript)

    const serviceScript = document.createElement('script')
    serviceScript.type = 'application/ld+json'
    serviceScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Service',
      serviceType: 'Ceramic Coating Installation',
      name: 'Ceramic Coating San Diego — San Diego PPF Landing Page',
      description: 'Professional ceramic coating in San Diego for paint, vinyl wraps, Colored Paint Protection Film, and PPF. Hydrophobic protection, UV defense, gloss enhancement, and easier maintenance.',
      areaServed: ['San Diego', 'Vista', 'Oceanside', 'Carlsbad', 'San Marcos', 'Escondido', 'Encinitas', 'Poway', 'Del Mar', 'La Jolla', 'Rancho Santa Fe', 'North County San Diego'],
      provider: {
        '@type': 'LocalBusiness',
        name: 'Mr Wrap - AutoWrapSanDiego',
        telephone: '+1-858-282-8119',
        priceRange: '$$$',
        url: 'https://autowrapsandiego.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '2244 South Santa Fe Avenue, Suite A7',
          addressLocality: 'Vista',
          addressRegion: 'CA',
          postalCode: '92084',
          addressCountry: 'US'
        },
        areaServed: 'San Diego County'
      }
    })
    document.head.appendChild(serviceScript)

    const breadcrumbScript = document.createElement('script')
    breadcrumbScript.type = 'application/ld+json'
    breadcrumbScript.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://autowrapsandiego.com/' },
        { '@type': 'ListItem', position: 2, name: 'Paint Protection Film San Diego', item: 'https://autowrapsandiego.com/paint-protection-film-san-diego' },
        { '@type': 'ListItem', position: 3, name: 'Ceramic Coating San Diego', item: 'https://autowrapsandiego.com/ceramic-coating-san-diego' }
      ]
    })
    document.head.appendChild(breadcrumbScript)

    return () => {
      document.head.removeChild(faqScript)
      document.head.removeChild(serviceScript)
      document.head.removeChild(breadcrumbScript)
    }
  }, [])

  return (
    <>
      <CanonicalTag url="https://autowrapsandiego.com/ceramic-coating-san-diego" />
      <div className="pt-20 min-h-screen bg-black">

        {/* Hero */}
        <section className="bg-gradient-to-br from-black via-gray-900 to-black text-white py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-4xl sm:text-5xl font-bold mb-6 leading-tight">
                Ceramic Coating San Diego
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-4">
                The complete guide to ceramic coating in San Diego. Learn how a professional ceramic coating protects your paint, vinyl wrap, Colored Paint Protection Film, and PPF with hydrophobic water-beading, UV defense, easier cleaning, and a deeper, longer-lasting gloss.
              </p>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto mb-8">
                Serving Vista, Oceanside, Carlsbad, San Marcos, Escondido, Encinitas, Poway, Del Mar, La Jolla, Rancho Santa Fe, and all of San Diego County.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a href="tel:858-282-8119" className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                  <Phone size={18} />
                  858-282-8119
                </a>
                <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-semibold transition">
                  <Calendar size={18} />
                  Get a Free Quote
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-black border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <ol className="flex items-center gap-2 text-sm text-gray-400 flex-wrap">
              <li><a href="/" className="hover:text-cyan-400 transition">Home</a></li>
              <li className="text-gray-600">›</li>
              <li><a href="/paint-protection-film-san-diego" className="hover:text-cyan-400 transition">Paint Protection Film San Diego</a></li>
              <li className="text-gray-600">›</li>
              <li className="text-cyan-400 font-medium" aria-current="page">Ceramic Coating San Diego</li>
            </ol>
          </div>
        </nav>

        {/* Trust Strip */}
        <section className="bg-gray-950 border-b border-cyan-500/20 py-5">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Award className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">13 Years of Experience</p>
                  <p className="text-gray-400 text-xs">Coatings, wraps & PPF</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Droplets className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Hydrophobic Protection</p>
                  <p className="text-gray-400 text-xs">Premium ceramic coatings</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <MapPin className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Vista / North County SD</p>
                  <p className="text-gray-400 text-xs">Serving all San Diego County</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 flex-shrink-0">
                  <Sparkles className="text-cyan-400" size={22} />
                </div>
                <div>
                  <p className="text-white font-semibold text-sm leading-tight">Tesla · Corvette · Jeep</p>
                  <p className="text-gray-400 text-xs">Trucks, luxury & exotic builds</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Summary */}
        <section className="py-16 bg-gray-900 border-y border-cyan-500/20">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-3 text-white text-center">Ceramic Coating San Diego — Quick Summary</h2>
            <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">Here is what every San Diego vehicle owner should understand about ceramic coating before booking a service.</p>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Long-lasting protection:</strong> A professional ceramic coating bonds to your surface and lasts years — not weeks like wax — protecting against UV, oxidation, and contaminants.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Hydrophobic & easy to clean:</strong> Water, dirt, and grime bead up and slide off, meaning faster washes, fewer water spots, and a vehicle that stays cleaner longer.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Works on more than paint:</strong> Ceramic coating can be applied over paint, vinyl wraps, Colored Paint Protection Film, and PPF for a complete protection package.</p>
              </div>
              <div className="flex items-start gap-3 bg-black p-5 rounded-xl border border-cyan-500/20">
                <Check className="text-cyan-400 mt-1 flex-shrink-0" size={22} />
                <p className="text-gray-300"><strong className="text-white">Not a rock chip shield:</strong> Ceramic resists swirls and chemical stains but does not stop rock chips. For impact protection, pair it with Paint Protection Film.</p>
              </div>
            </div>
          </div>
        </section>

        {/* What Is Ceramic Coating */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">What Is Ceramic Coating?</h2>
            <div className="space-y-5 text-gray-300 leading-relaxed text-lg">
              <p>
                Ceramic coating is a liquid polymer — made primarily of silica (SiO2) and other nano-ceramic compounds — that chemically bonds to your vehicle's surface. Once it cures, it forms a durable, semi-permanent protective layer that becomes part of the surface rather than simply sitting on top of it like wax.
              </p>
              <p>
                Unlike traditional waxes and sealants that wash away within weeks, a professional ceramic coating creates a hardened, glass-like shield that lasts for years. This layer is <strong className="text-white">hydrophobic</strong> (it repels water), <strong className="text-white">UV-resistant</strong> (it fights sun fading), and <strong className="text-white">chemically resistant</strong> (it shrugs off bird droppings, bug acids, and environmental fallout that would otherwise etch into paint).
              </p>
              <p>
                In San Diego's intense sun and coastal salt air, an unprotected vehicle is constantly under attack from UV rays, water spots, and airborne contaminants. Ceramic coating is one of the most effective ways to keep a vehicle looking new with dramatically less maintenance — which is exactly why it has become a standard finishing step on premium paint, wraps, and Paint Protection Film installations.
              </p>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">Benefits of Ceramic Coating</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">A quality ceramic coating delivers protection and convenience that wax simply cannot match. Here is what makes it worth the investment for San Diego drivers.</p>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Droplets className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Hydrophobic Water-Beading</h3>
                <p className="text-gray-400 text-sm leading-relaxed">The slick, glass-like surface causes water to bead and roll off, carrying dirt with it. This self-cleaning effect means fewer water spots and faster washes.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Sun className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">UV Protection</h3>
                <p className="text-gray-400 text-sm leading-relaxed">San Diego sun is relentless. Ceramic coating blocks UV rays that cause paint and wraps to fade, oxidize, and lose their shine over time.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Sparkles className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Gloss Enhancement</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Ceramic coating adds remarkable depth, clarity, and a wet-looking shine. Colors look richer and the finish stays brilliant for years.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Check className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Easier Maintenance & Cleaning</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Dirt and grime struggle to adhere to a coated surface. Washing becomes quicker, and your vehicle stays cleaner longer between washes.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Shield className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Chemical & Stain Resistance</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Bird droppings, bug splatter, tree sap, and industrial fallout are far less likely to etch into a coated surface, protecting your finish.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Award className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Light Scratch Resistance</h3>
                <p className="text-gray-400 text-sm leading-relaxed">The hardened layer adds resistance to swirl marks and wash-induced micro-scratches, helping keep the surface flawless.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Clock className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Long-Term Value</h3>
                <p className="text-gray-400 text-sm leading-relaxed">By preserving the finish and reducing wear, ceramic coating helps maintain your vehicle's appearance and resale value over the years.</p>
              </div>
              <div className="bg-black p-6 rounded-xl border border-cyan-500/20">
                <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-2 w-fit mb-4">
                  <Car className="text-cyan-400" size={22} />
                </div>
                <h3 className="text-white font-bold mb-2">Versatile Application</h3>
                <p className="text-gray-400 text-sm leading-relaxed">Works on paint, vinyl wraps, Colored Paint Protection Film, PPF, glass, wheels, and trim — for a fully protected vehicle.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Mid-page CTA */}
        <section className="py-14 bg-gradient-to-r from-cyan-600 to-cyan-500">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-4 text-white">Protect Your Investment with Ceramic Coating</h2>
            <p className="text-lg text-white/90 mb-6">Our Vista team will assess your vehicle and recommend the right ceramic coating package for your paint, wrap, or PPF.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call 858-282-8119
              </a>
              <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-10 rounded-lg transition inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Free Quote
              </button>
            </div>
          </div>
        </section>

        {/* What Ceramic Coating Can Be Applied To */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">What Can Ceramic Coating Be Applied To?</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">One of the biggest advantages of ceramic coating is its versatility. It is not just for paint — it enhances and protects nearly every exterior surface and finish.</p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Ceramic Coating for Paint</h3>
                <p className="text-gray-300">The most common application. A ceramic coating over factory or repainted surfaces locks in gloss, repels water, blocks UV fading, and makes the paint dramatically easier to maintain. It is the ideal final step after a paint correction to keep your finish flawless for years.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Ceramic Coating for Vinyl Wraps</h3>
                <p className="text-gray-300">Ceramic coating can be safely applied over <a href="/vinyl-wraps" className="text-cyan-400 hover:underline">vinyl wraps</a> to add hydrophobic protection and UV resistance. It helps protect satin and matte finishes from staining, makes the wrap easier to clean, and extends the life of the film — a smart upgrade for any color change wrap.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Ceramic Coating for Colored Paint Protection Film</h3>
                <p className="text-gray-300">Pairing ceramic coating with <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored Paint Protection Film</a> combines the rock chip protection and self-healing of Colored PPF with the hydrophobic, easy-clean, UV-resistant benefits of ceramic. This is one of the most complete protection packages available for high-value vehicles in San Diego.</p>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                <h3 className="text-2xl font-bold mb-3 text-white">Ceramic Coating for Paint Protection Film (PPF)</h3>
                <p className="text-gray-300">Applying ceramic coating over <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">Paint Protection Film</a> is highly recommended. While modern PPF already self-heals, a ceramic topcoat enhances hydrophobicity, reduces staining, boosts gloss, and makes the film easier to clean — the perfect finishing layer for a fully protected vehicle.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Myths & Misconceptions */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">Ceramic Coating Myths & Misconceptions</h2>
            <p className="text-gray-400 text-center mb-12 max-w-3xl mx-auto">There is a lot of misinformation about ceramic coating online. Here is the honest truth so you can set the right expectations.</p>
            <div className="space-y-5">
              {[
                { myth: 'Ceramic coating makes your car scratch-proof.', truth: 'False. Ceramic coating resists light swirls and wash marks, but it does NOT stop rock chips or deep scratches. For physical impact protection, you need Paint Protection Film (PPF).' },
                { myth: 'Ceramic coating means you never have to wash your car again.', truth: 'False. Coated vehicles still need regular washing — they just stay cleaner longer and are much easier to wash. Maintenance is reduced, not eliminated.' },
                { myth: 'Ceramic coating lasts forever.', truth: 'False. While semi-permanent and far more durable than wax, ceramic coatings last 2–5 years depending on the product, application, and care. They are not truly permanent.' },
                { myth: 'A DIY spray ceramic is the same as a professional coating.', truth: 'False. Consumer spray products offer light, short-term hydrophobic effects. Professional coatings require proper surface prep, correction, and curing — delivering far stronger bonding and longevity.' },
                { myth: 'Ceramic coating fixes existing scratches and swirls.', truth: 'False. Ceramic coating seals in the current condition of your paint. Any existing defects should be corrected first; otherwise they get locked in under the coating.' },
                { myth: 'Ceramic coating replaces the need for PPF.', truth: 'False. They serve different purposes. Ceramic handles gloss, UV, and chemical/hydrophobic protection; PPF handles physical rock chip and impact protection. The best results often combine both.' }
              ].map((item, i) => (
                <div key={i} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <div className="flex items-start gap-3 mb-3">
                    <X className="text-red-400 mt-1 flex-shrink-0" size={20} />
                    <p className="text-white font-bold">Myth: {item.myth}</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <Check className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                    <p className="text-gray-300 text-sm leading-relaxed">{item.truth}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Ceramic Coating vs Wax */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Ceramic Coating vs Wax — Comparison</h2>
            <p className="text-gray-400 text-center mb-10 max-w-3xl mx-auto">Wax has been the traditional choice for decades, but ceramic coating outperforms it in nearly every meaningful way. Here is how they compare.</p>
            <div className="overflow-x-auto rounded-xl border border-cyan-500/20">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-900">
                    <th className="p-4 text-white font-bold border-b border-cyan-500/20">Feature</th>
                    <th className="p-4 text-cyan-400 font-bold border-b border-cyan-500/20">Ceramic Coating</th>
                    <th className="p-4 text-gray-300 font-bold border-b border-cyan-500/20">Wax</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Longevity</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">2–5 years</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Weeks to a few months</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Hydrophobic Effect</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Strong & long-lasting</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Mild & short-lived</td>
                  </tr>
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">UV Protection</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Excellent</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Limited</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Chemical Resistance</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">High</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Low</td>
                  </tr>
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Gloss</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Deep, glass-like, lasting</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Warm shine, fades quickly</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Maintenance</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Easy — self-cleaning effect</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Frequent reapplication needed</td>
                  </tr>
                  <tr className="bg-black">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Application</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Professional prep & curing</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Simple, frequent DIY</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold">Best Use Case</td>
                    <td className="p-4 text-gray-300">Long-term, low-maintenance protection</td>
                    <td className="p-4 text-gray-400">Quick, temporary shine</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Ceramic Coating vs PPF */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Ceramic Coating vs Paint Protection Film — Comparison</h2>
            <p className="text-gray-400 text-center mb-4 max-w-3xl mx-auto">Ceramic coating and Paint Protection Film are often compared, but they solve different problems. Many San Diego owners use them together for complete protection.</p>
            <p className="text-gray-300 text-center mb-10 max-w-3xl mx-auto">
              Learn more about <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">Paint Protection Film San Diego</a> to understand which solution — or combination — is right for your vehicle.
            </p>
            <div className="overflow-x-auto rounded-xl border border-cyan-500/20">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-black">
                    <th className="p-4 text-white font-bold border-b border-cyan-500/20">Feature</th>
                    <th className="p-4 text-cyan-400 font-bold border-b border-cyan-500/20">Ceramic Coating</th>
                    <th className="p-4 text-orange-400 font-bold border-b border-cyan-500/20">Paint Protection Film (PPF)</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="bg-gray-900">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Rock Chip Protection</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">No</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Yes — physical impact barrier</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Self-Healing</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">No</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Yes — heals light scratches with heat</td>
                  </tr>
                  <tr className="bg-gray-900">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Hydrophobic Effect</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Excellent</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Good (better with ceramic topcoat)</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">UV Protection</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Excellent</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Excellent</td>
                  </tr>
                  <tr className="bg-gray-900">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Gloss Enhancement</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">High</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Available in gloss & matte</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Thickness</td>
                    <td className="p-4 text-gray-400 border-b border-gray-800">Microns — chemical layer</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Thick urethane (~8 mil)</td>
                  </tr>
                  <tr className="bg-gray-900">
                    <td className="p-4 text-white font-semibold border-b border-gray-800">Typical Investment</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Lower</td>
                    <td className="p-4 text-gray-300 border-b border-gray-800">Higher</td>
                  </tr>
                  <tr className="bg-gray-900/50">
                    <td className="p-4 text-white font-semibold">Best Use Case</td>
                    <td className="p-4 text-gray-300">Gloss, easy cleaning, UV & chemical defense</td>
                    <td className="p-4 text-gray-300">Physical rock chip & impact protection</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-gray-400 mt-8">
              The best protection often combines both: <a href="/paint-protection-film-san-diego" className="text-cyan-400 hover:underline">PPF</a> on high-impact areas, ceramic coating over the entire vehicle. Explore <a href="/colored-ppf-san-diego" className="text-cyan-400 hover:underline">Colored PPF San Diego</a> for color change plus protection.
            </p>
          </div>
        </section>

        {/* How Long It Lasts */}
        <section className="py-20 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-6 text-white text-center">How Long Does Ceramic Coating Last?</h2>
            <div className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20 mb-10">
              <div className="flex items-start gap-4">
                <Clock className="text-cyan-400 mt-1 flex-shrink-0" size={28} />
                <div>
                  <p className="text-gray-300 text-lg leading-relaxed mb-3">
                    A professionally applied ceramic coating typically lasts <strong className="text-white">2 to 5 years</strong>, with premium multi-layer coatings lasting even longer. Consumer-grade spray products last only weeks to a few months. The difference comes down to product quality, surface preparation, and proper curing — which is why professional application matters.
                  </p>
                  <p className="text-gray-400 leading-relaxed">
                    In San Diego's sunny, coastal climate, longevity is influenced by sun exposure, salt air, and how the vehicle is maintained. With proper care, a quality coating delivers years of protection and a brilliant finish.
                  </p>
                </div>
              </div>
            </div>
            <h3 className="text-2xl font-bold mb-6 text-white text-center">What Affects Ceramic Coating Longevity</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: 'Product Quality & Layers', body: 'Premium professional-grade coatings and multi-layer applications last significantly longer than single-layer or consumer spray products.' },
                { title: 'Surface Preparation', body: 'Proper decontamination and paint correction before application ensure a strong chemical bond. Skipped prep dramatically shortens coating life.' },
                { title: 'Sun & UV Exposure', body: 'San Diego\'s intense year-round sun gradually degrades any coating. Vehicles parked outdoors full-time wear faster than garaged ones.' },
                { title: 'Wash Habits', body: 'Gentle hand washing with pH-neutral soap preserves the coating. Harsh chemicals, automated brush washes, and abrasive towels reduce lifespan.' },
                { title: 'Coastal Salt & Contaminants', body: 'Salt air, road grime, bird droppings, and industrial fallout all stress the coating. Prompt removal of contaminants extends its life.' },
                { title: 'Maintenance & Toppers', body: 'Periodic maintenance washes and occasional ceramic topper applications refresh hydrophobic performance and extend overall longevity.' }
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20">
                  <div className="flex items-start gap-3">
                    <Info className="text-cyan-400 mt-1 flex-shrink-0" size={20} />
                    <div>
                      <h4 className="text-white font-bold mb-2">{item.title}</h4>
                      <p className="text-gray-400 text-sm leading-relaxed">{item.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-4 text-white text-center">Ceramic Coating FAQs — San Diego</h2>
            <p className="text-gray-400 text-center mb-12 max-w-2xl mx-auto">Common questions about ceramic coating, what it protects, and what to expect.</p>
            <div className="space-y-5">
              {[
                {
                  q: 'What is ceramic coating?',
                  a: 'Ceramic coating is a liquid polymer made primarily of silica (SiO2) that chemically bonds to a vehicle\'s surface, forming a durable, semi-permanent protective layer. Once cured, it creates a hydrophobic, UV-resistant, glossy shield that protects against contaminants, light scratches, oxidation, and chemical stains — far beyond what wax can offer.'
                },
                {
                  q: 'How long does ceramic coating last in San Diego?',
                  a: 'A professionally applied ceramic coating typically lasts 2 to 5 years, with premium multi-layer coatings lasting even longer. In San Diego\'s sunny, coastal climate, longevity depends on UV exposure, wash habits, and maintenance. Proper care maximizes lifespan.'
                },
                {
                  q: 'Is ceramic coating better than wax?',
                  a: 'Yes. Wax lasts only weeks to a few months and provides minimal protection. Ceramic coating chemically bonds to the surface and lasts years, offering superior hydrophobic properties, UV defense, chemical resistance, and a deeper, longer-lasting gloss.'
                },
                {
                  q: 'Does ceramic coating prevent scratches and rock chips?',
                  a: 'Ceramic coating adds hardness and resists light swirl marks and wash-induced scratches, but it does NOT stop rock chips or deep scratches. For physical impact protection, Paint Protection Film (PPF) is required. Many owners combine PPF on high-impact areas with ceramic coating over the rest of the vehicle.'
                },
                {
                  q: 'Can you ceramic coat a vinyl wrap?',
                  a: 'Yes. Ceramic coating can be applied over vinyl wraps to add hydrophobic protection, UV resistance, and easier cleaning while extending the life of the wrap. It helps protect satin and matte finishes from staining. We use coatings formulated to be safe for wrap films.'
                },
                {
                  q: 'Can you ceramic coat Colored Paint Protection Film?',
                  a: 'Absolutely. Applying ceramic coating over Colored Paint Protection Film combines the rock chip and self-healing protection of Colored PPF with the hydrophobic, easy-clean, UV-resistant benefits of ceramic — one of the most complete protection packages available for high-value vehicles.'
                },
                {
                  q: 'Should I ceramic coat my PPF (Paint Protection Film)?',
                  a: 'Yes — ceramic coating over PPF is highly recommended. While modern PPF already has self-healing properties, adding a ceramic topcoat enhances hydrophobicity, reduces staining, makes the film easier to clean, and boosts gloss. It is the ideal finishing layer.'
                },
                {
                  q: 'Is ceramic coating worth it?',
                  a: 'For most San Diego vehicle owners, yes. Ceramic coating dramatically reduces maintenance, protects against UV fading and water spots in our coastal/sunny climate, enhances gloss, and helps preserve resale value. It is a worthwhile investment for anyone who wants their vehicle to look its best with far less effort.'
                },
                {
                  q: 'Does ceramic coating make a car easier to clean?',
                  a: 'Yes. The hydrophobic surface causes water, dirt, and grime to bead up and slide off rather than sticking. This means faster washes, fewer water spots, and less contamination buildup. Coated vehicles stay cleaner longer between washes.'
                }
              ].map((item, i) => (
                <div key={i} className="bg-black p-6 rounded-xl border border-cyan-500/20">
                  <h3 className="text-xl font-bold mb-3 text-white">{item.q}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Mr Wrap */}
        <section className="py-20 bg-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-4xl font-bold mb-12 text-white text-center">Why Choose Mr Wrap for Ceramic Coating in San Diego</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-5xl mx-auto">
              {[
                { icon: Award, title: '13 Years of Experience', body: '13 years of professional coating, wrap, and PPF installation for San Diego vehicles. Experience shows in the finish.' },
                { icon: MapPin, title: 'Vista / North County SD Location', body: 'Conveniently located at 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084 — easy access from Oceanside, Carlsbad, San Marcos, Encinitas, and greater San Diego.' },
                { icon: Droplets, title: 'Premium Ceramic Products', body: 'We use only top-tier, professional-grade ceramic coatings — never consumer sprays. Proper prep, application, and curing for maximum longevity.' },
                { icon: Sparkles, title: 'Tesla, Corvette, Jeep & Luxury Experience', body: 'From Tesla\'s soft paint to Corvette\'s complex body lines and exotic supercars, we have specific experience with the vehicles San Diego drivers care about.' },
                { icon: Shield, title: 'Complete Protection Packages', body: 'We combine ceramic coating with PPF and Colored Paint Protection Film for complete, layered protection tailored to your vehicle and goals.' },
                { icon: Check, title: 'Quality-First Process', body: 'Every vehicle receives thorough decontamination, optional paint correction, precise application, and a detailed quality inspection before we call it done.' }
              ].map((item, i) => (
                <div key={i} className="bg-gray-900 p-8 rounded-xl border border-cyan-500/20">
                  <div className="bg-cyan-500/20 w-14 h-14 rounded-full flex items-center justify-center mb-5 border border-cyan-500">
                    <item.icon className="text-cyan-400" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-white">{item.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{item.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Service Area */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center flex items-center justify-center gap-3">
              <MapPin className="text-cyan-400" size={30} />
              Ceramic Coating — Serving San Diego County
            </h2>
            <p className="text-gray-300 text-lg mb-8 text-center">
              Based in Vista, we apply premium ceramic coatings to paint, wraps, Colored Paint Protection Film, and PPF throughout San Diego County and North County.
            </p>
            <div className="grid md:grid-cols-3 gap-4 text-gray-300 text-sm">
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">North County Coastal</h3>
                <ul className="space-y-1">
                  <li>• Oceanside</li>
                  <li>• Carlsbad</li>
                  <li>• Encinitas</li>
                  <li>• Del Mar</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">North County Inland</h3>
                <ul className="space-y-1">
                  <li>• Vista (Shop Location)</li>
                  <li>• San Marcos</li>
                  <li>• Escondido</li>
                  <li>• Rancho Santa Fe</li>
                  <li>• Poway</li>
                </ul>
              </div>
              <div className="bg-black p-4 rounded-lg border border-cyan-500/20">
                <h3 className="font-bold text-white mb-2">Greater San Diego</h3>
                <ul className="space-y-1">
                  <li>• San Diego</li>
                  <li>• La Jolla</li>
                  <li>• Mission Valley</li>
                  <li>• Downtown SD</li>
                  <li>• All SD County</li>
                </ul>
              </div>
            </div>
            <p className="text-gray-300 text-center mt-8">
              <strong className="text-white">Shop Address:</strong> 2244 South Santa Fe Avenue, Suite A7, Vista, CA 92084
            </p>
          </div>
        </section>

        {/* Embedded Quote Form */}
        <section className="py-16 bg-gray-950">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
            <EmbeddedQuoteForm
              defaultService="ceramic-coating"
              heading="Get a Free Ceramic Coating Quote"
              subheading="Tell us about your vehicle — paint, wrap, or PPF. Our Vista team will recommend the right ceramic coating package and provide a personalized quote."
            />
          </div>
        </section>

        {/* Related Services */}
        <section className="py-16 bg-black">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold mb-8 text-white text-center">Related Services</h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                { href: '/paint-protection-film-san-diego', title: 'Paint Protection Film San Diego', desc: 'Clear self-healing PPF for ultimate rock chip and UV protection' },
                { href: '/colored-ppf-san-diego', title: 'Colored PPF San Diego', desc: 'Color change paint protection film — pair it with ceramic coating' },
                { href: '/vehicle-wrap-cost-san-diego', title: 'Vehicle Wrap Cost San Diego', desc: 'The definitive guide to vehicle wrap pricing in San Diego' },
                { href: '/ppf-vs-ceramic-coating-san-diego', title: 'PPF vs Ceramic Coating San Diego', desc: 'Which is right for you? Compare protection, cost, longevity, and more' },
                { href: '/tesla-wrap-san-diego', title: 'Tesla Wrap San Diego', desc: 'Dedicated Tesla wrap services — stealth, satin, and color change builds' },
                { href: '/tesla-ppf-san-diego', title: 'Tesla PPF San Diego', desc: 'Paint protection film tailored for Tesla\'s soft factory paint' }
              ].map((item, i) => (
                <a key={i} href={item.href} className="bg-gray-900 p-6 rounded-xl border border-cyan-500/20 hover:border-cyan-500 transition group">
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-cyan-400 transition">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-r from-cyan-500 to-cyan-600">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl font-bold mb-6 text-white">Ready to Protect Your Vehicle with Ceramic Coating?</h2>
            <p className="text-xl text-white/90 mb-4">
              Whether you want to coat fresh paint, a new wrap, or your Paint Protection Film, our Vista team will recommend the right package for your vehicle and goals.
            </p>
            <p className="text-lg text-white/80 mb-8">
              Call <strong>858-282-8119</strong> or request a free quote online — no pressure, no obligation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button onClick={() => { navigate('/contact'); setTimeout(() => window.scrollTo(0, 0), 100) }} className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                <Calendar size={20} />
                Get Free Quote
              </button>
              <a href="tel:858-282-8119" className="bg-white hover:bg-gray-100 text-gray-900 font-bold py-4 px-12 rounded-lg transition-all transform hover:scale-105 shadow-xl inline-flex items-center justify-center gap-2">
                <Phone size={20} />
                Call: 858-282-8119
              </a>
            </div>
          </div>
        </section>

      </div>
    </>
  )
}

export default CeramicCoatingSanDiegoPage
